import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BankSecurityEvent, Currency, SupplierProfileEvent } from 'app/profile/models/supplier.model';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseComponent } from 'app/profile/components/base.component';
import { select, Store } from '@ngrx/store';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { TslDialog } from '@tesla/ng-tds';
import { GetCurrency, GetSecurityEvent } from 'app/profile/store/app.actions';
import { SelectCurrencies, SelectSecurityEventData } from 'app/profile/store/app.selector';
import { takeUntil } from 'rxjs/operators';
import { isTokenEffective, validateAllFormFields } from 'app/shared/utils/utils';
import { DecrementLoaderCount, IncrementLoaderCount } from 'app/shared/store/actions';
import { LocalStorageService } from 'app/shared/services/index.service';
import { BANK_TOKEN } from 'app/shared/constants';
import { AuthData } from '@tesla/ng-auth';
import { I18nService } from '../../../../../shared/services/i18n.service';
@Component({
  selector: 'app-security-check',
  templateUrl: './security-check.component.html',
  styleUrls: ['./security-check.component.scss']
})
export class SecurityCheckComponent extends BaseComponent implements OnInit {
  bankSecurityForm: FormGroup;
  currencies: Currency[];
  supplierCode: string;
  bankChangeReasons: any[];
  profileEvent: SupplierProfileEvent = null;
  errors: Array<string>;
  attempts: BankSecurityEvent = null;
  currentUserEmail: string;
  requestType: string;
  onCloseExecuteNotAnimation:boolean = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<SecurityCheckComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private store: Store<SupplierProfileState>,
    private supplierStateService: SupplierStateService,
    private matDialog: MatDialog,
    private dialog: TslDialog,
    private translateService: I18nService,
    private storageService: LocalStorageService,
    private authData: AuthData) {
    super();
    this.supplierCode = dialogData.supplierCode;
    this.requestType = dialogData.requestType;
    this.currentUserEmail = this.authData.getUserInfo().email;
    this.onCloseExecuteNotAnimation = (dialogData?.onCloseExecuteNotAnimation != undefined && dialogData?.onCloseExecuteNotAnimation != null) ? dialogData?.onCloseExecuteNotAnimation : false;

    this.bankChangeReasons = [
      {
        Code: "AccountCompromised",
        Name: "Account compromised"
      },
      {
        Code: "SecondBankAccount",
        Name: "Adding a second bank account"
      },
      {
        Code: "SwitchingBankAccount",
        Name: "Switching bank accounts"
      },
      {
        Code: "FactoringAgent",
        Name: "Factoring agent/outsourced payments to a third party"
      }
    ];
    if(this.requestType?.toLowerCase() == "NameOrTINChange".toLowerCase()) {
      this.bankChangeReasons.push(
        {
          Code: "ReviewingBankInformationOnFile",
          Name: "Reviewing bank information on file"
        },
        {
          Code: "UpdateToBeneficiaryOrAccountHolderName",
          Name: "Update to beneficiary/account holder name"
        }
      );
    }
  }

  ngOnInit() {
    if (isTokenEffective(this.storageService.retrieve(`${BANK_TOKEN}:${this.supplierCode}`))) {
      this.dialogRef.close(true);
    }

    this.bankSecurityForm = this.fb.group({
      IsLastPaymentReceived: [null],
      LastPaymentReceived: [null, Validators.required],
      Currency: [null, Validators.required],
      ChangeReason: [null, Validators.required],
      AccountNumber: [null, Validators.required],
      Beneficiary: [null, Validators.required],
      AROutSourced: [null]
    });

    this.store.dispatch(new GetCurrency());

    this.bankSecurityForm.get('IsLastPaymentReceived').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(x => {
      if (!x) {
        this.bankSecurityForm.get('LastPaymentReceived').clearValidators();
        this.bankSecurityForm.get('LastPaymentReceived').setValue(null);
        this.bankSecurityForm.get('LastPaymentReceived').disable();

        this.bankSecurityForm.get('Currency').clearValidators();
        this.bankSecurityForm.get('Currency').setValue(null);
        this.bankSecurityForm.get('Currency').disable();
      } else {
        this.bankSecurityForm.get('LastPaymentReceived').enable();
        this.bankSecurityForm.get('LastPaymentReceived').setValidators(Validators.required);

        this.bankSecurityForm.get('Currency').enable();
        this.bankSecurityForm.get('Currency').setValidators(Validators.required);
      }
      this.bankSecurityForm.get('LastPaymentReceived').updateValueAndValidity();
      this.bankSecurityForm.get('Currency').updateValueAndValidity();
    });

    this.store.pipe(select(SelectSecurityEventData), takeUntil(this.ngUnsubscribe)).subscribe(securityEvent => {
      if (securityEvent) {
        this.attempts = securityEvent;
      }
    });

    this.store.pipe(select(SelectCurrencies), takeUntil(this.ngUnsubscribe)).subscribe(items => {
      this.currencies = items;
    });
  }

  get BankSecurityFormControls() {
    return {
      IsLastPaymentReceived: this.bankSecurityForm.get('IsLastPaymentReceived'),
      LastPaymentReceived: this.bankSecurityForm.get('LastPaymentReceived'),
      Currency: this.bankSecurityForm.get('Currency'),
      ChangeReason: this.bankSecurityForm.get('ChangeReason'),
      AccountNumber: this.bankSecurityForm.get('AccountNumber'),
      Beneficiary: this.bankSecurityForm.get('Beneficiary'),
      AROutSourced: this.bankSecurityForm.get('AROutSourced')
    };
  }

  getValidationMessages(controlName: string, displayName: string, control: FormControl) {
    if (control.dirty || control.touched) {
      if (control.hasError('required')) {
        return this.translateService.get(displayName) === null ? this.translateService.get('Required') : this.translateService.get(displayName) + ' ' + this.translateService.get('is required');
      }
    }
  }

  get showAttempts() {
    return this.attempts && this.attempts.wrongAttempts > 0 && this.attempts.wrongAttempts < this.attempts.maxAttempts ? true : false;
  }

  get showAttemptsMessage() {
    var count = this.attempts.maxAttempts - this.attempts.wrongAttempts;
    return this.translateService.get('You have') + ' ' + count + ' ' + (count > 1 ? this.translateService.get('remaining attempts') : this.translateService.get('remaining attempt'));
  }

  hasError(control: FormControl): boolean {
    return control.errors && (control.dirty || control.touched);
  }

  onNoClick(): void {

    if(this.onCloseExecuteNotAnimation != undefined && this.onCloseExecuteNotAnimation != null && this.onCloseExecuteNotAnimation == true)
    {
      this.dialogRef.close();
    }
    else
    {
      document
      .getElementsByClassName('animate__animated')[0]
      .classList.add('animate__slideOutRight');
      setTimeout(() => {
        this.dialogRef.close();
      }, 1000);
    }
  }

  submit() {
    this.profileEvent = null;
    if (this.bankSecurityForm.valid) {
      this.errors = null;

      let data = new SupplierProfileEvent();
      data.properties = Object.assign({}, this.bankSecurityForm.value);

      this.store.dispatch(new IncrementLoaderCount());
      this.supplierStateService.upsertBankSecurityAnswers(this.supplierCode, data)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(x => {
          if (x) {
            this.storageService.store(`${BANK_TOKEN}:${this.supplierCode}`, x.token);
            // when answers are correct, close dialog
            if (this.isPassed(x.profileEvent)) {

              if(this.onCloseExecuteNotAnimation != undefined && this.onCloseExecuteNotAnimation != null && this.onCloseExecuteNotAnimation == true)
              {
                this.dialogRef.close(true);
              }
              else
              {
                document
                .getElementsByClassName('animate__animated')[0]
                .classList.add('animate__slideOutRight');
                setTimeout(() => {
                  this.dialogRef.close(true);
                }, 1000);
              }
            }
            // when 'Locked'
            else if (this.isLocked(x.profileEvent)) {
              this.profileEvent = x.profileEvent;
            }
            // show errors
            else {
              this.errors = x.errors;
            }
            this.store.dispatch(new GetSecurityEvent(this.supplierCode));
          }
          this.store.dispatch(new DecrementLoaderCount());
        },
          (error: string) => {
            localStorage.removeItem(`${BANK_TOKEN}:${this.supplierCode}`);
            this.store.dispatch(new DecrementLoaderCount());
            console.error(`Could not submit bank security answers. Error message: ${error}`);
          });
    } else {
      validateAllFormFields(this.bankSecurityForm);
    }
  }

  isPassed(event: SupplierProfileEvent) { return event.status === 'Passed'; }

  isLocked(event: SupplierProfileEvent) { return event.status === 'Locked'; }
}
